import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  Image,
  ImageField,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { icon_arrow_right } from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { mapSharedPropertiesToFields } from 'common/getters';
import { SitecoreFieldTypes } from 'common/types/sitecore';
import { DynamicHeading } from 'composables/shared/Heading/DynamicHeading';
import { ComponentProps } from 'lib/component-props';

type SAPromoSpotProps = {
  fields: {
    promoCards: MappedSAPromoSpotCard[];
    eyebrowText: { value: string };
    headline: { value: string };
    isMainHeading: Field<boolean>;
    description: { value: string };
  };
} & ComponentProps;

type SAPromoSpot = {
  promoCards: SAPromoSpotCard[];
  eyebrowText: TextField;
  headline: TextField;
  isMainHeading: Field<boolean>;
  description: TextField;
};

type SAPromoSpotCard = {
  backgroundImage: ImageField;
  logo: ImageField;
  text: TextField;
  buttonLink: LinkField;
  id: string;
};

type MappedSAPromoSpotCard = {
  backgroundImgSrc: string;
  backgroundImgDisplayName: string;
  logoSrc: string;
  logoDisplayName: string;
  text: string;
  buttonLink: string;
  id: string;
};

type SAPromoCardsProps = {
  items: MappedSAPromoSpotCard[];
};

export const getStaticProps: GetStaticComponentProps = async (rendering: ComponentRendering) => {
  const fields = rendering.fields;
  const mappedFields = mapSharedPropertiesToFields<SAPromoSpot>(fields as SitecoreFieldTypes);

  const refinedPromoCards = mappedFields.promoCards?.map((card) => ({
    backgroundImgSrc: card.backgroundImage?.value?.src || '',
    backgroundImgDisplayName: card.backgroundImage?.value?.displayName || '',
    logoSrc: card.logo?.value?.src || '',
    logoDisplayName: card.logo?.value?.displayName || '',
    text: sanitize((card.text?.value as string) || ''),
    buttonLink: card.buttonLink?.value?.href || '',
    id: card.id,
  }));

  return {
    fields: {
      ...mappedFields,
      promoCards: refinedPromoCards,
      headline: {
        value: sanitize((mappedFields.headline?.value as string) || ''),
      },
      description: {
        value: sanitize((mappedFields.description?.value as string) || ''),
      },
    },
  };
};

const SAPromoCards = ({ items }: SAPromoCardsProps) => {
  return (
    <div className="mt-8 flex basis-full flex-col gap-4 md:flex-row">
      {items.map((item) => {
        const CardContent = () => (
          <article className="relative flex h-[320px] w-full flex-col items-center justify-center rounded-2xl bg-blue-900 md:h-[540px]">
            {item.backgroundImgSrc && (
              <img
                src={item.backgroundImgSrc}
                alt={item.backgroundImgDisplayName}
                className="absolute inset-0 z-0 size-full rounded-2xl object-cover object-center"
                loading="lazy"
              />
            )}

            {item.logoSrc && (
              <div className="absolute left-0 top-10 z-10 flex h-[70px] w-[178px] items-center justify-center rounded-r-full bg-white md:top-16">
                <Image
                  className="h-[70px] w-auto"
                  field={{
                    src: item.logoSrc,
                    alt: item.logoDisplayName,
                  }}
                />
              </div>
            )}
            <div
              className="relative z-10 text-2xl font-bold text-white"
              dangerouslySetInnerHTML={{ __html: item.text as string }}
            />

            {item.buttonLink && (
              <div className="absolute bottom-0 z-10 flex w-full justify-end pb-4 pr-4">
                <div className="flex size-10 items-center justify-center rounded-full bg-yellow">
                  <JabraIcon className="size-6 text-black filter" Icon={icon_arrow_right} />
                </div>
              </div>
            )}
          </article>
        );

        return item.buttonLink ? (
          <a
            key={item.id}
            href={item.buttonLink}
            className="w-full rounded-2xl hover:no-underline md:pb-0 md:pt-0"
          >
            <CardContent />
          </a>
        ) : (
          <div key={item.id} className="w-full rounded-2xl md:pb-0 md:pt-0">
            <CardContent />
          </div>
        );
      })}
    </div>
  );
};

const SAPromoSpot: React.FC<SAPromoSpotProps> = ({ fields }) => {
  const { promoCards, eyebrowText, headline, isMainHeading, description } = fields;

  return (
    <div className="container-fluid">
      <div className="container mb-0 !py-0 text-center">
        {eyebrowText?.value && <Eyebrow className="mb-5" text={eyebrowText.value} align="center" />}

        {headline?.value && (
          <DynamicHeading isMainHeading={isMainHeading?.value} className="heading-2">
            <span dangerouslySetInnerHTML={{ __html: headline.value }} />
          </DynamicHeading>
        )}

        {description?.value && (
          <div
            className="paragraph-large"
            dangerouslySetInnerHTML={{ __html: description.value }}
          />
        )}
        <SAPromoCards items={promoCards} />
      </div>
    </div>
  );
};

export default withDatasourceCheck()(SAPromoSpot);
